import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { mangaList } from '@/mocks/manga';

const featuredIds = [2, 6, 12, 21, 4];
const featuredManga = featuredIds.map(id => mangaList.find(m => m.id === id)).filter(Boolean) as typeof mangaList;

const categorySlugMap: Record<string, string> = {
  lianai: 'lianai-romance',
  dongzuo: 'dongzuo-action',
  qihuan: 'qihuan-fantasy',
  xuanyi: 'xuanyi-thriller',
  gaoxiao: 'gaoxiao-comedy',
  BL: 'bl-comics',
  GL: 'gl-comics',
  rexia: 'rexia-recommend',
};

export default function HeroBanner() {
  const [current, setCurrent] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const goTo = useCallback((index: number) => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrent(index);
    setTimeout(() => setIsTransitioning(false), 600);
  }, [isTransitioning]);

  const next = useCallback(() => {
    goTo((current + 1) % featuredManga.length);
  }, [current, goTo]);

  useEffect(() => {
    const timer = setInterval(next, 5000);
    return () => clearInterval(timer);
  }, [next]);

  const manga = featuredManga[current];

  return (
    <section className="relative w-full h-[420px] md:h-[520px] overflow-hidden" aria-label="精选推荐轮播">
      <div
        className="absolute inset-0 bg-cover bg-center transition-all duration-700 ease-in-out"
        style={{ backgroundImage: `url(${manga.coverImage})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/70" />

      <div className="relative z-10 h-full max-w-7xl mx-auto px-4 md:px-6 flex flex-col justify-end pb-14 md:pb-18">
        <div className={`transition-all duration-500 ${isTransitioning ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'}`}>
          <div className="flex items-center gap-3 mb-3">
            <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
              manga.status === '连载中'
                ? 'bg-primary-500 text-background-50'
                : 'bg-foreground-700 text-background-50'
            }`}>
              {manga.status}
            </span>
            <span className="text-background-50/80 text-sm">
              <i className="ri-star-fill text-accent-500 mr-1"></i>
              {manga.rating}
            </span>
            <span className="text-background-50/80 text-sm">
              <i className="ri-eye-line mr-1"></i>
              {(manga.views / 10000).toFixed(0)}万阅读
            </span>
          </div>

          <h2 className="text-2xl md:text-4xl font-heading font-bold text-background-50 mb-3">
            {manga.title}
          </h2>
          <p className="text-background-50/80 text-sm md:text-base max-w-xl line-clamp-2 mb-5">
            {manga.description}
          </p>

          <div className="flex items-center gap-3 flex-wrap">
            <Link
              to={`/article/${manga.id}`}
              className="px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap"
            >
              立即阅读
            </Link>
            <Link
              to={`/category/${categorySlugMap[manga.category] || 'rexia-recommend'}`}
              className="px-6 py-2.5 bg-background-50/20 hover:bg-background-50/30 text-background-50 backdrop-blur-sm rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap"
            >
              查看更多同类
            </Link>
          </div>
        </div>

        <div className="flex items-center gap-2 mt-6">
          {featuredManga.map((_, index) => (
            <button
              key={index}
              onClick={() => goTo(index)}
              className={`transition-all duration-300 rounded-full ${
                index === current
                  ? 'w-8 h-2.5 bg-primary-500'
                  : 'w-2.5 h-2.5 bg-background-50/40 hover:bg-background-50/60'
              }`}
              aria-label={`第${index + 1}张`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}